import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { UserService } from 'src/app/services/user/user.service';
import Swal from 'sweetalert2';

/**
 * Modal COMPARTIDO "Confirmar reporte a la aseguradora".
 * Misma lógica/comportamiento que la Consola de Seguros: dry-run (Simular),
 * comparación distribuido vs a reportar, visor del JSON/endpoint y confirmación
 * final. Se usa tanto en la consola como en la vista individual del pago.
 *
 * Entrada: `payment` (fila con al menos `.code`; el resto es informativo y se
 * deriva en el backend) y `open`. Salidas: `openChange` (sincroniza el flag del
 * padre) y `reported` (se emite cuando el reporte se ejecuta con éxito, para que
 * el padre refresque su listado).
 */
@Component({
  selector: 'app-insurance-report-confirm',
  templateUrl: './insurance-report-confirm.component.html'
})
export class InsuranceReportConfirmComponent implements OnChanges {
  @Input() payment: any = null;
  @Input() open = false;
  @Output() openChange = new EventEmitter<boolean>();
  @Output() reported = new EventEmitter<any>();

  prDryRunning = false;
  prDryRunResult: any = null;
  prDetail: any = null;
  prDetailLoading = false;
  prReporting = false;

  private wasOpen = false;

  constructor(private _user: UserService, private sanitizer: DomSanitizer) {}

  ngOnChanges(_changes: SimpleChanges): void {
    // Al pasar a abierto, inicializa: carga el detalle y auto-simula (igual que la consola).
    if (this.open && !this.wasOpen) {
      this.wasOpen = true;
      this.prDryRunResult = null;
      this.prDryRunning = false;
      this.prReporting = false;
      if (this.payment?.code) {
        this.loadReportDetail(this.payment.code);
        this.simulateReport();
      }
    } else if (!this.open) {
      this.wasOpen = false;
    }
  }

  /** ¿El dry-run indica un bloqueo/error (parcial, sin recibo, etc.)? → no permitir confirmar. */
  get prReportBlocked(): boolean {
    const r = this.prDryRunResult;
    if (!r) return false;
    return r.success === false || !!r.error;
  }

  /** Filas de comparación: lo que el pago DISTRIBUYÓ vs lo que se REPORTARÁ a SM. */
  get prComparisonRows(): any[] {
    const r = this.prDryRunResult;
    if (!r || !Array.isArray(r.distribution)) return [];
    const reported = Array.isArray(r.report_summary) ? r.report_summary : [];
    return r.distribution.map((d: any) => {
      const rep = reported.find((x: any) => String(x.receipt_number) === String(d.receipt_number))
        || reported.find((x: any) => String(x.cuota) === String(d.cuota));
      const match = !!rep && String(rep.receipt_number) === String(d.receipt_number) && String(rep.cuota) === String(d.cuota);
      return {
        cuota_dist: d.cuota,
        receipt_dist: d.receipt_number,
        monto_cuota: d.monto_cuota,
        distribuido: d.distribuido,
        pending: d.pending,
        cuota_rep: rep?.cuota ?? null,
        receipt_rep: rep?.receipt_number ?? null,
        match,
        sm_proximo_cuota: d.sm_proximo_cuota,
        sm_proximo_recibo: d.sm_proximo_recibo,
        sm_difiere: d.sm_proximo_cuota != null && String(d.sm_proximo_cuota) !== String(d.cuota)
      };
    });
  }

  loadReportDetail(paymentCode: string): void {
    this.prDetail = null;
    this.prDetailLoading = true;
    this._user.getInsuranceReportDetail(paymentCode).subscribe({
      next: (resp: any) => {
        this.prDetailLoading = false;
        this.prDetail = resp?.data || null;
      },
      error: () => {
        this.prDetailLoading = false;
        this.prDetail = null;
      }
    });
  }

  prIsMulti(row: any): boolean {
    const codes = row?.insurance_report?.installment_codes;
    return Array.isArray(codes) && codes.length > 1;
  }

  closeReportModal(): void {
    if (this.prReporting || this.prDryRunning) return;
    this.open = false;
    this.wasOpen = false;
    this.prDryRunResult = null;
    this.prDetail = null;
    this.openChange.emit(false);
  }

  // Simulación (dry run): ejecuta TODA la cadena en el backend resolviendo póliza,
  // cuotas y el payload exacto que se enviaría a la aseguradora, SIN persistir
  // ni notificar nada. Devuelve la traza real para inspeccionarla en el modal.
  simulateReport(): void {
    const row = this.payment;
    if (!row || this.prReporting) return;
    this.prDryRunning = true;
    this.prDryRunResult = null;
    this._user.retryOrCreateInsuranceReport(row.code, true).subscribe({
      next: (resp: any) => {
        this.prDryRunning = false;
        this.prDryRunResult = resp?.data?.trace || resp?.data || resp;
      },
      error: (error: any) => {
        this.prDryRunning = false;
        this.prDryRunResult = {
          error: error?.error?.message || error?.message || 'Error en la simulación',
          details: error?.error || null
        };
      }
    });
  }

  get prDryRunJson(): string {
    return this.prDryRunResult ? JSON.stringify(this.prDryRunResult, null, 2) : '';
  }

  copyDryRunResult(): void {
    try {
      navigator.clipboard?.writeText(this.prDryRunJson);
      Swal.fire({ icon: 'success', title: 'Traza copiada', timer: 1200, showConfirmButton: false });
    } catch {
      // noop
    }
  }

  // Confirmar y ejecutar el reporte a la aseguradora. Pide CONFIRMACIÓN FINAL
  // explícita (acción irreversible que notifica a SM).
  async confirmReport(): Promise<void> {
    const row = this.payment;
    if (!row) return;

    if (this.prReportBlocked) {
      Swal.fire('No permitido', 'Hay un bloqueo (pago parcial, cuota sin recibo o discrepancia). No se puede reportar.', 'warning');
      return;
    }

    const rows = this.prComparisonRows;
    const resumen = rows.length
      ? rows.map((r: any) => `• Cuota <b>${r.cuota_rep ?? r.cuota_dist}</b> · recibo <b>${r.receipt_rep ?? r.receipt_dist}</b> · ${(r.pending ?? 0)} USD`).join('<br>')
      : 'la(s) cuota(s) de este pago';

    const confirm = await Swal.fire({
      icon: 'warning',
      title: '¿Confirmar reporte a la aseguradora?',
      html: `Esta acción <b>notifica el pago a Seguros Mercantil</b>, queda registrada a tu nombre y <b>no se puede deshacer</b>.<br><br>Se reportará:<br>${resumen}`,
      showCancelButton: true,
      confirmButtonText: 'Sí, reportar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#2563eb',
      cancelButtonColor: '#6b7280',
      reverseButtons: true,
      focusCancel: true
    });
    if (!confirm.isConfirmed) return;

    this.prReporting = true;
    this._user.retryOrCreateInsuranceReport(row.code).subscribe({
      next: (resp: any) => {
        this.prReporting = false;
        this.open = false;
        this.wasOpen = false;
        this.openChange.emit(false);
        if (resp?.success) {
          Swal.fire('Éxito', resp?.message || 'Pago reportado exitosamente', 'success');
        } else {
          Swal.fire('Atención', resp?.message || 'No se pudo reportar el pago', 'warning');
        }
        this.reported.emit(resp);
      },
      error: (error: any) => {
        this.prReporting = false;
        const msg = error?.error?.message || error?.message || 'Error desconocido';
        Swal.fire('Error', `No se pudo reportar: ${msg}`, 'error');
      }
    });
  }

  personPhoto(row: any): any {
    const url = row?.person?.photo_url;
    if (url) return this.sanitizer.bypassSecurityTrustUrl(url);
    const female = (row?.person?.gender === 'F');
    return this.sanitizer.bypassSecurityTrustUrl(
      female ? '../assets/public/images/avatara-female.png' : '../assets/public/images/avatar.png'
    );
  }

  prProductLabel(row: any): string {
    const t = (row?.insurance_report?.type_product || row?.typeProduct || '').toString().trim();
    if (!t) return 'Seguro';
    const map: { [k: string]: string } = {
      SEGUROS_MERCANTIL: 'Seguros Mercantil',
      SEGURO_MERCANTIL: 'Seguros Mercantil'
    };
    if (map[t]) return map[t];
    return t.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (c: string) => c.toUpperCase());
  }

  // Vista previa en JSON de exactamente qué se envía y qué deriva el backend.
  get prReportJsonPreview(): string {
    const p = this.payment;
    if (!p) return '{}';
    const ir = p.insurance_report || {};
    const preview = {
      metodo: 'POST',
      endpoint: `/api/admin/insurance-reports/retry-or-create/${p.code}`,
      request_body: {},
      nota: 'El cuerpo va vacío: solo viaja el código del pago en la URL. El backend deriva el resto desde la BD y reporta a la aseguradora.',
      datos_del_pago: {
        codigo_pago: p.code,
        poliza: p.policy_code || ir.policy_code || null,
        producto: this.prProductLabel(p),
        type_product: ir.type_product || p.typeProduct || null,
        monto: p.amount ?? ir.total_amount ?? null,
        tasa: p.rate ?? ir.change_rate ?? null,
        banco: p.bank?.name || p.bank?.code || null,
        referencia: ir.reference || p.reference || null,
        fecha_pago: p.date || null,
        tipo: this.prIsMulti(p)
          ? `Multi-cuota (${ir.installment_codes?.length || 0} cuotas)`
          : 'Cuota única',
        installment_codes: ir.installment_codes || null,
        intentos: `${ir.current_intento || 0} / ${ir.limite || 10}`
      }
    };
    return JSON.stringify(preview, null, 2);
  }

  copyReportPreview(): void {
    try {
      navigator.clipboard?.writeText(this.prReportJsonPreview);
      Swal.fire({ icon: 'success', title: 'JSON copiado', timer: 1200, showConfirmButton: false });
    } catch {
      // noop
    }
  }
}
